/**
 * Configure call pipeline: session merge → auth config encode → client invoke.
 *
 * Symmetric with {@link KeyVaultAuthPipeline} and {@link ExplicitAuthPipeline} for
 * initial authenticator setup (no auth proof).
 *
 * @typedef {import('./KeyVaultAuthPipeline.js').KeyVaultAuthPipeline} KeyVaultAuthPipeline
 * @typedef {import('../config/AuthConfigEncoder.js').AuthConfigEncoder} AuthConfigEncoder
 *
 * @module internal/auth/pipelines/AuthConfigPipeline
 */

/**
 * @public
 */
export class AuthConfigPipeline {
  /**
   * @public
   * @param {{ keyVaultAuthPipeline: KeyVaultAuthPipeline; authConfigEncoder: AuthConfigEncoder }} deps
   */
  constructor({ keyVaultAuthPipeline, authConfigEncoder }) {
    this._keyVaultAuthPipeline = keyVaultAuthPipeline;
    this._authConfigEncoder = authConfigEncoder;
  }

  /**
   * @public
   * @async
   * @param {Record<string, unknown>} [options={}]
   * @param {ConfigureCallDescriptor} descriptor
   * @returns {Promise<unknown>}
   */
  async configure(options = {}, descriptor) {
    const resolved = await this._keyVaultAuthPipeline.mergeVaultOptions(
      options,
      descriptor.resolveFlags ?? {}
    );
    const { authConfig } = this._authConfigEncoder.encode({
      authConfig: descriptor.buildAuthConfigInput(resolved),
      authenticatorAddr: descriptor.authenticatorAddr
    });
    return descriptor.invoke({ keyVaultAddr: resolved.keyVaultAddr, authConfig });
  }
}
